// Molecule Maker: atoms have hands (bonding slots). Put atoms in the bowl and mix; if every
// hand is held and the recipe is a real molecule, it forms. Ported from the widget, with its
// 13 molecules kept, wrapped in lesson-then-do-it steps and an Explore mode.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';

interface AtomDef { n: string; c: string; t: string; h: number; r: number }
const AT: Record<string, AtomDef> = {
  H: { n: 'Hydrogen', c: '#ffffff', t: '#444441', h: 1, r: 14 },
  O: { n: 'Oxygen', c: '#E24B4A', t: '#ffffff', h: 2, r: 18 },
  C: { n: 'Carbon', c: '#444441', t: '#ffffff', h: 4, r: 18 },
  N: { n: 'Nitrogen', c: '#378ADD', t: '#ffffff', h: 3, r: 18 },
  Cl: { n: 'Chlorine', c: '#639922', t: '#ffffff', h: 1, r: 19 },
  Na: { n: 'Sodium', c: '#7F77DD', t: '#ffffff', h: 1, r: 20 },
};
const ATOM_ORDER = ['H', 'O', 'C', 'N', 'Cl', 'Na'];

interface Recipe { f: string; n: string; a: string[]; p: [number, number][]; b: [number, number, number][]; d: string }
export const RX: Recipe[] = [
  { f: 'H2', n: 'Hydrogen gas', a: ['H', 'H'], p: [[-0.5, 0], [0.5, 0]], b: [[0, 1, 1]], d: 'The lightest thing in the whole universe. Rockets burn it for fuel.' },
  { f: 'O2', n: 'Oxygen gas', a: ['O', 'O'], p: [[-0.5, 0], [0.5, 0]], b: [[0, 1, 2]], d: 'The part of the air your body needs. Take a deep breath, that is this!' },
  { f: 'N2', n: 'Nitrogen gas', a: ['N', 'N'], p: [[-0.5, 0], [0.5, 0]], b: [[0, 1, 3]], d: 'Most of the air around you is this. It is very calm and does not react much.' },
  { f: 'Cl2', n: 'Chlorine gas', a: ['Cl', 'Cl'], p: [[-0.55, 0], [0.55, 0]], b: [[0, 1, 1]], d: 'A yellow-green gas. A tiny bit of chlorine is what makes a pool smell like a pool.' },
  { f: 'H2O', n: 'Water', a: ['O', 'H', 'H'], p: [[0, 0.25], [-0.79, -0.36], [0.79, -0.36]], b: [[0, 1, 1], [0, 2, 1]], d: 'You drink it, swim in it, and it falls from clouds as rain. Oxygen holds one hydrogen in each hand.' },
  { f: 'CO2', n: 'Carbon dioxide', a: ['C', 'O', 'O'], p: [[0, 0], [-1, 0], [1, 0]], b: [[0, 1, 2], [0, 2, 2]], d: 'The fizz in soda, and what you breathe out. Plants breathe it in to grow.' },
  { f: 'NH3', n: 'Ammonia', a: ['N', 'H', 'H', 'H'], p: [[0, 0.3], [-0.87, -0.2], [0.87, -0.2], [0, -0.7]], b: [[0, 1, 1], [0, 2, 1], [0, 3, 1]], d: 'Very stinky! It is in some cleaning sprays, and farmers use it to feed plants.' },
  { f: 'CH4', n: 'Methane', a: ['C', 'H', 'H', 'H', 'H'], p: [[0, 0], [0, 1], [0, -1], [-1, 0], [1, 0]], b: [[0, 1, 1], [0, 2, 1], [0, 3, 1], [0, 4, 1]], d: 'A gas that burns in stoves to cook dinner. Cows burp it out, too!' },
  { f: 'NaCl', n: 'Table salt', a: ['Na', 'Cl'], p: [[-0.6, 0], [0.6, 0]], b: [[0, 1, 1]], d: 'The same salt you sprinkle on food. Sodium gives chlorine a tiny piece of itself, and then they stick together.' },
  { f: 'HCl', n: 'Hydrogen chloride', a: ['H', 'Cl'], p: [[-0.55, 0], [0.55, 0]], b: [[0, 1, 1]], d: 'Mixed into water, this is the acid in your tummy that helps break up food.' },
  { f: 'H2O2', n: 'Hydrogen peroxide', a: ['H', 'O', 'O', 'H'], p: [[-1.3, 0.45], [-0.5, 0], [0.5, 0], [1.3, -0.45]], b: [[0, 1, 1], [1, 2, 1], [2, 3, 1]], d: 'Almost water, with one extra oxygen. It fizzes on a scrape to help clean it.' },
  { f: 'O3', n: 'Ozone', a: ['O', 'O', 'O'], p: [[0, 0.4], [-0.87, -0.25], [0.87, -0.25]], b: [[0, 1, 1], [0, 2, 2]], d: 'High up in the sky, a layer of ozone protects us from the sun\'s strongest rays.' },
  { f: 'CO', n: 'Carbon monoxide', a: ['C', 'O'], p: [[-0.5, 0], [0.5, 0]], b: [[0, 1, 3]], d: 'You cannot see or smell it, and it is poisonous. That is why homes have detectors for it.' },
];

/** molecules found across rounds this session */
const FOUND = new Set<string>();

interface Step {
  lesson: string;
  task: string;
  kind: 'make' | 'choice';
  want?: (r: Recipe) => boolean;   // for 'make'
  wantHint: string;
  choices?: string[];
  answer?: number;
  why: string;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { lesson: 'Atoms hold hands. The orange dots are an atom\'s hands. Hydrogen has one hand, oxygen has two. A molecule forms when every hand is holding another hand.', task: 'Make water: two hydrogens hold hands with one oxygen. Put them in the bowl and press Mix.', kind: 'make', want: r => r.f === 'H2O', wantHint: 'Water is one oxygen and two hydrogens. Check the bowl: O, H, H.', why: 'Oxygen has two hands, and each hydrogen has one. Two hydrogens fill both of oxygen\'s hands: water.' },
    { lesson: 'Carbon has four hands. Oxygen has two. When two oxygens each hold carbon with both hands, all four of carbon\'s hands are full.', task: 'Make carbon dioxide: one carbon and two oxygens.', kind: 'make', want: r => r.f === 'CO2', wantHint: 'Carbon dioxide is C plus O plus O.', why: 'Each oxygen holds carbon with both hands. That is a double bond, drawn as two lines.' },
    { lesson: 'Two of the same atom can hold hands too. Most of the air you breathe is pairs like this.', task: 'Make a gas from two of the SAME atom.', kind: 'make', want: r => r.a.length === 2 && r.a[0] === r.a[1], wantHint: 'Try two hydrogens, two oxygens, two nitrogens or two chlorines.', why: 'Pairs like O2 and N2 are what the air is mostly made of.' },
  ],
  2: [
    { lesson: 'Count the hands. The number of hands tells you how many partners an atom wants.', task: 'How many hydrogens does one carbon want?', kind: 'choice', choices: ['4', '2', '1', '3'], answer: 0, wantHint: '', why: 'Carbon has four hands, so it holds four hydrogens. That molecule is methane.' },
    { lesson: 'Nitrogen has three hands. It can hold three hydrogens, or pair up with another nitrogen using all three hands at once.', task: 'Make any molecule that has nitrogen in it.', kind: 'make', want: r => r.a.includes('N'), wantHint: 'Nitrogen plus three hydrogens makes ammonia. Two nitrogens make nitrogen gas.', why: 'Nitrogen\'s three hands make ammonia (NH3) or nitrogen gas (N2, a triple bond).' },
    { lesson: 'Now fill all four of carbon\'s hands with hydrogens.', task: 'Make methane: one carbon with four hydrogens.', kind: 'make', want: r => r.f === 'CH4', wantHint: 'Methane is C plus four H.', why: 'Four hydrogens, one each, fill all four of carbon\'s hands. Methane is the gas in a stove.' },
  ],
  3: [
    { lesson: 'An atom\'s hands are really empty spaces where it can share. Oxygen has two empty spaces, so it looks for two partners.', task: 'Why does oxygen want two partners?', kind: 'choice', choices: ['It has two empty slots', 'It is heavy', 'It is red', 'It is big'], answer: 0, wantHint: '', why: 'Two empty slots means two hands. Color, size and weight do not decide it.' },
    { lesson: 'Bigger molecules need more hands. The atom with the most hands can hold the most partners.', task: 'Make the biggest molecule you can: at least five atoms.', kind: 'make', want: r => r.a.length >= 5, wantHint: 'Start with the atom that has the most hands, then fill every hand.', why: 'Carbon\'s four hands plus four hydrogens is five atoms: methane is the biggest one here.' },
    { lesson: 'When two atoms hold on with two hands each, that is a double bond. It is drawn as two lines.', task: 'Make a molecule that has a double bond (two lines between two atoms).', kind: 'make', want: r => r.b.some(b => b[2] >= 2), wantHint: 'Oxygen has two hands. Two oxygens, or carbon with two oxygens, make double bonds.', why: 'Oxygen gas, ozone and carbon dioxide all have double bonds: two hands holding two hands.' },
  ],
};

interface BowlAtom { s: string; x: number; y: number; vx: number; vy: number; tx?: number; ty?: number; idx?: number }

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let explore = false;
  let attempts = 0, correct = 0, missed = false;
  const t0 = performance.now();
  let tStep = performance.now();

  const MAX = 8, HGT = 260;
  let bowl: BowlAtom[] = [];
  let mode: 'bowl' | 'result' = 'bowl';
  let hit: Recipe | null = null;
  let t = 0, shake = 0;

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const tray = h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' } });
  const stage = h('div');
  const card = h('div.card', { style: { display: 'none' } });
  const actions = h('div.grid2');
  const choices = h('div.grid2');
  const foundLine = h('p.muted.small.center', null, '');
  host.append(lesson, task, tray, stage, card, actions, choices, foundLine);
  const S = makeSurface(stage, { height: HGT, draw: false });

  ATOM_ORDER.forEach(s => {
    const a = AT[s];
    tray.append(h('button.btn.small', { style: { justifyContent: 'flex-start', gap: '8px', padding: '6px 8px' }, onclick: () => add(s) },
      h('span', { style: { width: '26px', height: '26px', borderRadius: '50%', background: a.c, color: a.t, border: '1px solid #2C2C2A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', flex: 'none' } }, s),
      h('span', { style: { textAlign: 'left', lineHeight: '1.15', fontSize: '13px' } }, a.n, h('br'), h('span.muted', { style: { fontSize: '11px' } }, `${a.h} hand${a.h > 1 ? 's' : ''}`)),
    ));
  });

  function renderActions() {
    clear(actions); clear(choices);
    const st = explore ? null : steps[si];
    if (st && st.kind === 'choice') {
      st.choices!.forEach((c, i) => choices.append(h('button.choice', { onclick: () => answer(i) }, c)));
      return;
    }
    actions.append(
      h('button.btn.primary', { onclick: mix }, 'Mix it!'),
      h('button.btn', { onclick: () => { if (mode === 'result') reset(false); bowl.pop(); if (!bowl.length) ctx.say('Put some atoms in the bowl, then press Mix.'); } }, 'Take one out'),
      h('button.btn.ghost', { onclick: () => reset(true) }, 'Empty bowl'),
      explore ? h('button.btn.ghost', { onclick: surprise }, 'Surprise me') : h('span'),
    );
  }

  function tally() {
    foundLine.textContent = `Molecules found: ${FOUND.size} of ${RX.length}` + (FOUND.size ? `. ${RX.filter(r => FOUND.has(r.f)).map(r => r.n).join(', ')}.` : '');
  }

  function counts(a: string[]): Record<string, number> { const c: Record<string, number> = {}; a.forEach(s => { c[s] = (c[s] || 0) + 1; }); return c; }
  function formula(a: string[]): string { const c = counts(a); return Object.keys(c).sort((x, y) => ['C', 'H'].indexOf(y) - ['C', 'H'].indexOf(x)).map(k => k + (c[k] > 1 ? String(c[k]) : '')).join(''); }

  function add(s: string) {
    if (mode === 'result') reset(false);
    if (bowl.length >= MAX) { ctx.say('The bowl is full! Mix it or take some out.'); return; }
    bowl.push({ s, x: S.w * 0.25 + ctx.rng() * S.w * 0.5, y: 70 + ctx.rng() * 110, vx: (ctx.rng() - 0.5) * 0.6, vy: (ctx.rng() - 0.5) * 0.6 });
    ctx.sound('pop');
  }
  function reset(clearAll: boolean) {
    mode = 'bowl'; hit = null; card.style.display = 'none';
    if (clearAll) { bowl = []; ctx.say('Put some atoms in the bowl, then press Mix.'); }
    else bowl.forEach(b => { b.vx = (ctx.rng() - 0.5) * 0.6; b.vy = (ctx.rng() - 0.5) * 0.6; delete b.tx; delete b.ty; });
  }
  function match(): Recipe | null {
    const c = counts(bowl.map(b => b.s));
    for (const r of RX) { const rc = counts(r.a); const ks = Object.keys(c).concat(Object.keys(rc)); if (ks.every(k => (c[k] || 0) === (rc[k] || 0))) return r; }
    return null;
  }
  function hint(): string {
    const c = counts(bowl.map(b => b.s));
    let best: { r: Recipe; adds: [string, number][]; rems: [string, number][] } | null = null, bc = 1e9;
    for (const r of RX) {
      const rc = counts(r.a); const adds: [string, number][] = [], rems: [string, number][] = [];
      const ks = new Set([...Object.keys(c), ...Object.keys(rc)]);
      ks.forEach(k => { const d = (rc[k] || 0) - (c[k] || 0); if (d > 0) adds.push([k, d]); if (d < 0) rems.push([k, -d]); });
      const cost = adds.reduce((s2, a) => s2 + a[1], 0) + rems.reduce((s2, a) => s2 + a[1], 0) * 1.5;
      if (cost < bc) { bc = cost; best = { r, adds, rems }; }
    }
    if (!best) return '';
    const nm = (k: string, n: number) => `${n} ${AT[k].n.toLowerCase()}${n > 1 ? 's' : ''}`;
    const b = best as { r: Recipe; adds: [string, number][]; rems: [string, number][] };
    if (b.adds.length && !b.rems.length) return `Close! Add ${b.adds.map(a => nm(a[0], a[1])).join(' and ')} to make ${b.r.n.toLowerCase()}.`;
    if (b.rems.length && !b.adds.length) return `Almost! Take out ${b.rems.map(a => nm(a[0], a[1])).join(' and ')} and you would have ${b.r.n.toLowerCase()}.`;
    return `Try ${b.r.n.toLowerCase()}: it needs ${b.r.a.join(' + ')}.`;
  }

  function mix() {
    if (mode === 'result') return;
    if (bowl.length === 0) { ctx.say('The bowl is empty. Tap some atoms first!'); return; }
    const st = explore ? null : steps[si];
    if (bowl.length === 1) { ctx.say(`${AT[bowl[0].s].n} is lonely. Atoms like to hold hands with other atoms.`); shake = 30; if (st) miss(st, ''); return; }
    const r = match();
    if (!r) { shake = 30; const hnt = hint(); ctx.say(`Those atoms do not stick together that way. ${hnt}`); ctx.sound('buzz'); if (st) miss(st, ''); return; }
    mode = 'result'; hit = r;
    const used: number[] = []; const cx = S.w / 2, cy = 125, U = Math.min(52, S.w / 8);
    r.a.forEach((s, i) => { for (let k = 0; k < bowl.length; k++) { if (bowl[k].s === s && !used.includes(k)) { used.push(k); bowl[k].tx = cx + r.p[i][0] * U; bowl[k].ty = cy - r.p[i][1] * U; bowl[k].idx = i; break; } } });
    clear(card);
    card.style.display = '';
    card.append(h('div.row', { style: { justifyContent: 'space-between', alignItems: 'baseline' } }, h('h3', null, r.n), h('span.muted.small', null, r.f)), h('p.small', null, r.d));
    const isNew = !FOUND.has(r.f);
    FOUND.add(r.f); tally();
    if (FOUND.size >= RX.length) ctx.charm('charm.molecules');
    if (st && st.kind === 'make') {
      if (st.want!(r)) pass(st);
      else { ctx.sound('pop'); ctx.say(`You made ${r.n.toLowerCase()}${isNew ? '!' : ' again.'} ${st.wantHint}`); miss(st, ''); }
    } else {
      ctx.sound(isNew ? 'ding' : 'pop');
      ctx.say(isNew ? `You made ${r.n.toLowerCase()}!` : `You made ${r.n.toLowerCase()} again!`);
    }
  }

  function surprise() {
    reset(true);
    const r = RX[Math.floor(ctx.rng() * RX.length)];
    r.a.slice().sort(() => ctx.rng() - 0.5).forEach(add);
    ctx.say('Here are some atoms. Press Mix and see what they make!');
  }

  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of ${steps.length}: ${st.task}`;
    missed = false; tStep = performance.now();
    reset(true);
    renderActions();
    ctx.speak(st.task);
  }
  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: 'chemistry', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `mol:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    si++;
    if (si >= steps.length) {
      explore = true;
      task.textContent = 'Round complete. The bowl is yours: find all 13 molecules!';
      renderActions();
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Molecules found so far: ${FOUND.size} of ${RX.length}`] }), 1600);
      return;
    }
    setTimeout(showStep, 1800);
  }
  function miss(st: Step, extra: string) {
    if (!missed) { attempts++; missed = true; ctx.report({ skill: 'chemistry', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `mol:${tier}:${si}` }); }
    if (extra) ctx.say(extra);
    void st;
  }
  function answer(i: number) {
    const st = steps[si];
    if (i === st.answer) pass(st);
    else { ctx.sound('buzz'); miss(st, 'Count the orange dots on that atom. Each dot is one hand.'); }
  }

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (mode !== 'bowl') return;
      for (let i = bowl.length - 1; i >= 0; i--) { if (Math.hypot(bowl[i].x - p.x, bowl[i].y - p.y) < AT[bowl[i].s].r + 6) { bowl.splice(i, 1); ctx.sound('tick'); return; } }
    },
  });

  function atom(c: CanvasRenderingContext2D, x: number, y: number, s: string, free: number) {
    const a = AT[s];
    c.fillStyle = a.c; c.strokeStyle = '#2C2C2A'; c.lineWidth = 1.2; c.beginPath(); c.arc(x, y, a.r, 0, Math.PI * 2); c.fill(); c.stroke();
    c.fillStyle = a.t; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText(s, x, y + 0.5);
    for (let k = 0; k < free; k++) { const ang = -Math.PI / 2 + k * 2 * Math.PI / a.h; c.fillStyle = '#EF9F27'; c.beginPath(); c.arc(x + Math.cos(ang) * (a.r + 5), y + Math.sin(ang) * (a.r + 5), 2.6, 0, Math.PI * 2); c.fill(); }
  }

  function update(dt: number) { t += dt; }

  function draw() {
    const c = S.ctx, w = S.w, H = S.h;
    c.fillStyle = '#F1EFE8'; c.fillRect(0, 0, w, H);
    if (mode === 'bowl') {
      c.fillStyle = '#D3D1C7'; c.beginPath(); c.ellipse(w / 2, 205, w * 0.42, 30, 0, 0, Math.PI); c.fill();
      c.strokeStyle = '#B4B2A9'; c.lineWidth = 2; c.beginPath(); c.ellipse(w / 2, 205, w * 0.42, 30, 0, 0, Math.PI * 2); c.stroke();
      const sx = shake > 0 ? Math.sin(t * 40) * 4 : 0;
      for (const b of bowl) {
        b.x += b.vx + sx * 0.3; b.y += b.vy; b.vx += (ctx.rng() - 0.5) * 0.08; b.vy += (ctx.rng() - 0.5) * 0.08; b.vx *= 0.98; b.vy *= 0.98;
        if (b.x < 40) { b.x = 40; b.vx = Math.abs(b.vx); } if (b.x > w - 40) { b.x = w - 40; b.vx = -Math.abs(b.vx); }
        if (b.y < 50) { b.y = 50; b.vy = Math.abs(b.vy); } if (b.y > 185) { b.y = 185; b.vy = -Math.abs(b.vy); }
      }
      for (const b of bowl) atom(c, b.x + sx, b.y, b.s, AT[b.s].h);
      c.fillStyle = '#5F5E5A'; c.font = '700 13px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
      c.fillText(bowl.length ? `In the bowl: ${formula(bowl.map(b => b.s))}. Tap an atom to take it out.` : 'Tap atoms above to drop them in.', w / 2, H - 12);
      if (shake > 0) shake--;
    } else if (hit) {
      let done = true;
      for (const b of bowl) { if (b.tx === undefined) continue; b.x += (b.tx - b.x) * 0.12; b.y += (b.ty! - b.y) * 0.12; if (Math.abs(b.tx - b.x) > 0.5) done = false; }
      const by = bowl.filter(b => b.tx !== undefined).sort((a, b) => a.idx! - b.idx!);
      if (done) {
        c.strokeStyle = '#2C2C2A'; c.lineWidth = 3;
        for (const bd of hit.b) {
          const a = by[bd[0]], o = by[bd[1]]; if (!a || !o) continue;
          const dx = o.x - a.x, dy = o.y - a.y, L = Math.hypot(dx, dy) || 1, nx = -dy / L * 5, ny = dx / L * 5;
          for (let k = 0; k < bd[2]; k++) { const off = k - (bd[2] - 1) / 2; c.beginPath(); c.moveTo(a.x + nx * off, a.y + ny * off); c.lineTo(o.x + nx * off, o.y + ny * off); c.stroke(); }
        }
        const wob = Math.sin(t * 2) * 2;
        for (const b of by) atom(c, b.x, b.y + wob * 0.3, b.s, 0);
        c.fillStyle = '#5F5E5A'; c.font = '700 13px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
        c.fillText('Lines are hands holding on. Two lines = holding with both hands.', w / 2, H - 12);
      } else for (const b of by) atom(c, b.x, b.y, b.s, 0);
    }
  }

  const loop = makeLoop(update, draw);
  loop.start();
  tally();
  showStep();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.molecule-maker',
  title: 'Molecule Maker',
  blurb: 'Atoms hold hands. Fill every hand and see which of 13 real molecules you made.',
  category: 'science',
  skills: ['chemistry'],
  levelRange: [1, 10],
  unlock: { stars: 10 },
  mount,
};
export default mod;
